import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { layout, media } from '@prisjakt/ui-base';
import { usePriceRowContext } from '../../../price-row-context';
import CampaignBadge from 'src/components/campaign-badge';
import { ShippingLabel } from './shipping-label';
import { PriceLabel } from './price-label';
import { HomeDeliveryBadge } from './home-delivery-badge';
import { RecommendedRetailerBadge } from './recommended-retailer-badge';
import { PriceTypeNoticeBadge } from './price-type-badge';
import { CountryBadge } from './country-badge';
import { StockBadge } from './stock-badge';
import { DeliveryTime } from '../delivery-time';
import { AlternativePrice } from 'src/types';
import { getCampaignByOfferId } from '../../../utils';
import * as S from './styles';
import { useStockStatusExperiment } from 'src/hooks/experiments';
import { getDeliveryDays, ShippingType } from '../delivery-time/utils';

interface IStockPriceInfo {
  alternativeOffer?: AlternativePrice;
}

export const StockPriceInfo: React.FC<IStockPriceInfo> = props => {
  const { alternativeOffer } = props;
  const { campaign, offer } = usePriceRowContext();
  const { featured, isInternationalPrice, deliveryOptions, store } = offer;

  // Get props from alternative offer if supplied
  const id = alternativeOffer ? alternativeOffer.id : offer.priceId;
  const price = alternativeOffer ? alternativeOffer.price : offer.price;
  const shipping = alternativeOffer
    ? alternativeOffer.shipping
    : offer.shipping;
  const stock = alternativeOffer ? alternativeOffer.stock : offer.stock;
  const authorizedDealer = alternativeOffer
    ? null // Not applicable for alternative offers
    : offer.authorizedDealer;
  const priceType = alternativeOffer
    ? null // Not applicable for alternative offers
    : offer.priceType;
  const homeDelivery = alternativeOffer
    ? null // Not applicable for alternative offers
    : deliveryOptions &&
      deliveryOptions.find(option => option && option.type === 'HOME_DELIVERY');

  const activeCampaign = getCampaignByOfferId(id, campaign);

  // Experiment on showing Stock Status vs Delivery time (Delivery time is default)
  const [showStockStatus, setShowStockStatus] = useState(false);
  const experimentVariant = useStockStatusExperiment();
  useEffect(() => {
    const hasDeliveryTime =
      shipping && Boolean(getDeliveryDays(ShippingType.CHEAPEST, shipping));
    setShowStockStatus(!hasDeliveryTime && experimentVariant);
  }, [experimentVariant, shipping]);

  return (
    <S.Column>
      <S.FirstRow>
        {price && featured && <ShippingLabel price={price} />}

        {activeCampaign && (
          <DesktopWrapper>
            <CampaignBadge colors={activeCampaign.media.colors} />
          </DesktopWrapper>
        )}

        {price && <PriceLabel price={price} activeCampaign={activeCampaign} />}
      </S.FirstRow>
      {featured && (
        <S.SecondRow>
          <DesktopWrapper>
            {authorizedDealer && <RecommendedRetailerBadge />}
            {priceType && <PriceTypeNoticeBadge priceType={priceType} />}
            {store?.countryCode && isInternationalPrice && (
              <CountryBadge countryCode={store?.countryCode} />
            )}
            <StockBadge
              status={stock?.status}
              statusText={stock?.statusText}
              showLabel={showStockStatus}
            />
            {homeDelivery && <HomeDeliveryBadge />}
          </DesktopWrapper>

          <MobileWrapper>
            <StockBadge
              status={stock?.status}
              statusText={stock?.statusText}
              showLabel={showStockStatus}
            />
          </MobileWrapper>
          {!showStockStatus && (
            <DeliveryTime shipping={shipping} stockStatus={stock?.status} />
          )}
        </S.SecondRow>
      )}
    </S.Column>
  );
};

const DesktopWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${layout.measurements.spacing025};

  ${media.smallOnly`
    display: none;
  `}
`;

const MobileWrapper = styled.div`
  ${media.mediumUp`
    display: none;
  `}
`;
